import { useLocalStorage } from "./useLocalStorage";

interface SidebarPreferences {
  hiddenItems: string[];
  itemOrder: Record<string, string[]>;
  version: number;
}

interface SidebarPreferencesActions {
  hideItem: (itemId: string) => void;
  showItem: (itemId: string) => void;
  reorderItems: (groupId: string, newOrder: string[]) => void;
  resetToDefaults: () => void;
}

export function useSidebarPreferences(): [
  SidebarPreferences,
  (
    value:
      | SidebarPreferences
      | ((prev: SidebarPreferences) => SidebarPreferences)
  ) => void,
  SidebarPreferencesActions,
] {
  const defaultPreferences: SidebarPreferences = {
    hiddenItems: [],
    itemOrder: {},
    version: 1,
  };

  const [preferences, setPreferences] = useLocalStorage<SidebarPreferences>(
    "sidebar-preferences",
    defaultPreferences
  );

  const hideItem = (itemId: string) => {
    setPreferences((prev) => ({
      ...prev,
      hiddenItems: [...new Set([...prev.hiddenItems, itemId])],
    }));
  };

  const showItem = (itemId: string) => {
    setPreferences((prev) => ({
      ...prev,
      hiddenItems: prev.hiddenItems.filter((id) => id !== itemId),
    }));
  };

  const reorderItems = (groupId: string, newOrder: string[]) => {
    setPreferences((prev) => ({
      ...prev,
      itemOrder: {
        ...prev.itemOrder,
        [groupId]: newOrder,
      },
    }));
  };

  const resetToDefaults = () => {
    setPreferences(defaultPreferences);
  };

  return [
    preferences,
    setPreferences,
    { hideItem, showItem, reorderItems, resetToDefaults },
  ];
}
